import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import WarningIcon from '@material-ui/icons/Warning';
import BusinessIcon from '@material-ui/icons/Business';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    fontSize: 60,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  businessOffIcon: {
    fontSize: 60,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  warningBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

const CompanyDeactivatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const fetchCompany = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const companyDoc = doc(firestore, 'companies', id);
      const companySnapshot = await getDoc(companyDoc);
      
      if (companySnapshot.exists()) {
        setCompany({
          id: companySnapshot.id,
          ...companySnapshot.data()
        });
      } else {
        // Si l'entreprise n'existe pas, utiliser des données de démonstration
        setCompany({
          id: id,
          name: 'Voyage Express',
          email: 'contact@voyage-express.com',
          phone: '+33123456789',
          address: '123 Rue de Paris',
          city: 'Paris',
          postalCode: '75001',
          country: 'France',
          status: 'Active',
        });
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de l\'entreprise',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmDeactivate = async () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const companyDoc = doc(firestore, 'companies', id);
      await updateDoc(companyDoc, {
        status: 'Inactive',
        deactivatedAt: new Date(),
        deactivatedReason: 'Désactivée par l\'administrateur'
      });
      
      setSnackbar({
        open: true,
        message: 'Entreprise désactivée avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/companies');
      }, 1500);
      
    } catch (error) {
      console.error('Error deactivating company:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la désactivation de l\'entreprise',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancelDeactivate = () => {
    setOpenConfirmDialog(false);
  };

  const handleBack = () => {
    navigate('/admin/companies');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Désactiver une entreprise
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : company ? (
        <Paper className={classes.paper}>
          <Box className={classes.warningBox}>
            <BusinessIcon className={classes.businessOffIcon} />
            <Typography variant="h5" gutterBottom>
              Vous êtes sur le point de désactiver l'entreprise suivante :
            </Typography>
            <Typography variant="h6" gutterBottom>
              {company.name}
            </Typography>
            <Typography variant="body1" paragraph>
              Email: {company.email}
            </Typography>
            <Typography variant="body1" paragraph>
              Téléphone: {company.phone}
            </Typography>
            <Typography variant="body1" paragraph>
              Adresse: {company.address}, {company.city}, {company.postalCode}, {company.country}
            </Typography>
            <Typography variant="body1" paragraph>
              Statut actuel: {company.status}
            </Typography>
            
            <Typography variant="body1" paragraph style={{ marginTop: 16 }}>
              <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8, color: '#f44336' }} />
              La désactivation d'une entreprise aura les conséquences suivantes :
            </Typography>
            
            <Box textAlign="left" width="100%" maxWidth="600px" margin="0 auto">
              <Typography variant="body1" paragraph>
                • L'entreprise ne pourra plus être sélectionnée pour de nouveaux voyages
              </Typography>
              <Typography variant="body1" paragraph>
                • Les véhicules associés à cette entreprise seront marqués comme indisponibles
              </Typography>
              <Typography variant="body1" paragraph>
                • Les voyages déjà programmés avec cette entreprise resteront actifs
              </Typography>
              <Typography variant="body1" paragraph>
                • Les utilisateurs associés à cette entreprise conserveront leur accès
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph style={{ marginTop: 16 }}>
              Cette action peut être annulée ultérieurement en modifiant le statut de l'entreprise.
            </Typography>
            
            <Grid container spacing={2} justifyContent="center" style={{ marginTop: 16 }}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                >
                  Annuler
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDeactivate}
                >
                  Désactiver l'entreprise
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Entreprise non trouvée
          </Typography>
        </Paper>
      )}
      
      {/* Dialog de confirmation */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelDeactivate}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la désactivation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir désactiver l'entreprise {company?.name} ? 
            Cette action affectera tous les véhicules associés à cette entreprise.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeactivate} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDeactivate} color="secondary" autoFocus>
            Confirmer la désactivation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyDeactivatePage;
